"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonialsData = [{
  id: 1,
  quote: "\"We can’t say enough good things about BT Remodeling. Jon was God sent after a horrible experience with a previous contractor who left our project in shambles. From the moment we met we knew we were in great hands. He is professional, very knowledgeable about everything related to construction and he is committed to quality.\nHe is a pleasure to work with and he is a problem solver. His attention to detail is impeccable and his craftsmanship is exceptional. He goes above and beyond to ensure the work is done right.\nWhat truly sets BT Remodeling apart is its ability to restore confidence after a bad experience. Jon listened to our concerns, addressed every issue with care, and delivered results that exceeded our expectations. Our home now looks incredible and we owe it all to Jon’s skill and dedication.\nIf you’re looking for a licensed general contractor who’s trustworthy, skilled, and genuinely puts his clients first, look no further.\nWe will recommend Jon to anyone without hesitation! He truly lives by his motto: no job too big, no job too small, no homeowner left behind.\"",
  name: "SILVIA B.",
  company: "Kitchen, Bathroom & Main Floor Remodel",
  image: "https://modern-joey.10web.cloud/wp-content/uploads/2026/05/PXL_20250701_231727219.RAW-01.COVER_-scaled.jpg?v=kjgsl1hxm9f"
}, {
  id: 2,
  quote: "\"Jon and his team built a covered patio off the back of our house. They were incredibly professional, clean, and timely. Jon communicated with us the entire time and made sure we were happy every step of the way. They left the job site clean and tidy every day, and we are beyond pleased with the finished product. We would recommend BT Remodeling for any house project, large or small!\"",
  name: "MICHAELA P.",
  company: "Patio Cover",
  image: "https://modern-joey.10web.cloud/wp-content/uploads/2026/05/IMG_20210402_172344322-scaled.jpg?v=uc6uh6yxhgn"
}, {
  id: 3,
  quote: "\"I just got new flooring installed in my rental. He removed old tile, carpet, hauled it away and had the new floors in with the new trim in 2 days. Price was extremely reasonable and the floors turned out Awesome!\"",
  name: "KIM V.",
  company: "Flooring",
  image: "https://modern-joey.10web.cloud/wp-content/uploads/2026/05/IMG_20220804_162842180-scaled.jpg?v=n0fizo142pj"
}];
function AnimatedTestimonialCard({
  testimonial,
  index
}: {
  testimonial: (typeof testimonialsData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="shadow-lg border-border/60 overflow-hidden rounded-md bg-card">
        <CardContent className="p-8 md:p-10 flex flex-col gap-6">
          <Quote className="size-10 text-secondary rotate-180 shrink-0" aria-hidden="true" />
          <p className="text-lg md:text-xl font-default text-foreground leading-relaxed italic">
            "{testimonial.quote}"
          </p>
          <div className="flex items-center gap-4 mt-4">
            <SafeHydrate><Avatar className="size-14 border border-border rounded-md">
              <AvatarImage src={testimonial.image} alt={testimonial.name} className="aspect-square size-full object-cover !w-[80px]" />
              <AvatarFallback className="bg-secondary/20 text-primary font-bold font-default rounded-md">
                {testimonial.name.charAt(0)}
              </AvatarFallback>
            </Avatar></SafeHydrate>
            <div className="flex flex-col">
              <span className="font-bold text-primary font-default text-base tracking-wide uppercase">
                {testimonial.name}
              </span>
              <span className="text-sm text-muted-foreground font-default">
                {testimonial.company}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function Testimonials() {
  return <section data-section-id="233699" id="voices-of-satisfaction" className="w-full bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="flex flex-col gap-8 md:gap-12">
          {testimonialsData.map((testimonial, i) => <AnimatedTestimonialCard key={testimonial.id} testimonial={testimonial} index={i} />)}
        </div>
      </div>
    </section>;
}